"use client"

import styled from "@emotion/styled"
import { Box, Button } from "@mui/material"
import { useLocale } from "next-intl"
import Link from "next/link"
import { Desktop, Mobile, NewText } from "ui"
import ImageWithAlt from "ui/ImageWithAlt"
import { sharedColors } from "ui/theme/colors"
import { buttonClick, extractImage, extractProperty, getNonUmbracoUrl } from "utils"

const StyledContetnContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 0 16px;
  margin-bottom: 32px;

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 500px;

    margin-bottom: 60px;
    margin-inline-start: calc((100vw - 1280px) / 2);
  }
`

const StyledTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  p {
    margin: 0;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    align-items: center;
  }
`

const StyledGrid = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 0px;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: grid;

    grid-template-columns: 50vw 50vw;
    gap: 80px;
  }
`
const StyledDesktopImage = styled.div`
  height: calc(100vw / 360 * 570);
  position: relative;

  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: calc(100vw / 2 + 85px);
    height: 620px;
  }
`

type Props = {
  data: any
  children: React.ReactNode
}

export const AbsoluteImageWithText: React.FC<Props> = ({ data, children }) => {
  const locale = useLocale()
  const title = extractProperty(data, "headline")
  const description = extractProperty(data, "body")
  const link = extractProperty(data, "link")
  const image = extractImage(data, "image")
  const imageForMobile = extractImage(data, "imageForMobile")

  return (
    <StyledGrid>
      <StyledContetnContainer>
        {children}
        <StyledTitleContainer>
          <NewText
            desktopFontSize={40}
            desktopFontWeight="400"
            desktopLineHeight="110%"
            mobileFontSize={32}
            mobileFontWeight="400"
            mobileLineHeight={"112.5%"}
            component="div"
            color={sharedColors.black}
            dangerouslySetInnerHTML={{ __html: title }}
            margin={0}
          />
        </StyledTitleContainer>
        <NewText
          desktopFontSize={14}
          desktopFontWeight="400"
          desktopLineHeight="20px"
          mobileFontSize={12}
          mobileFontWeight="400"
          mobileLineHeight={"17px"}
          component="div"
          color={sharedColors.black}
          dangerouslySetInnerHTML={{ __html: description }}
        />
        {link[0] && (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: {
                xs: "flex-end",
                md: "flex-start",
              },
            }}
          >
            <Link
              href={getNonUmbracoUrl(link[0].url)}
              target={link[0].target}
              style={{
                textDecoration: "none",
                marginBlockStart: "24px",
              }}
            >
              <Desktop>
                <Button
                  variant="outlined"
                  size="large"
                  style={{ fontSize: "18px" }}
                  onClick={() => buttonClick(link[0]?.name, "טופס ESG")}
                >
                  {link[0]?.name}
                </Button>
              </Desktop>
              <Mobile>
                <Button variant="outlined" size="small" style={{ fontSize: "16px" }}>
                  {link[0]?.name}
                </Button>
              </Mobile>
            </Link>
          </Box>
        )}
      </StyledContetnContainer>
      <StyledDesktopImage>
        <Desktop>
          <ImageWithAlt
            image={image}
            fill
            style={{
              position: "absolute",
              insetInlineStart: "-180px",
              objectFit: "cover",
              objectPosition: "center",
              transform: locale === "en" ? "scaleX(-1)" : "scaleX(1)",
            }}
          />
        </Desktop>
        <Mobile>
          <ImageWithAlt
            image={imageForMobile}
            fill
            style={{
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
        </Mobile>
      </StyledDesktopImage>
    </StyledGrid>
  )
}

export default AbsoluteImageWithText
